import { pick } from 'lodash/fp';
import { AppClockService, createAction, lt, showSuccessAlert } from '@mews/framework';
import { RouteName } from '@mews-commander/enums';
import { ApplicationState, CommanderThunkAction } from '@mews-commander/types';
import { createPollerActions } from '@mews-commander/utils';
import { getCurrentEnterpriseId, getCurrentProfileId } from '@mews-commander/core';
import { fetchAccountingConfiguration } from '@mews-commander/legacy-entities/src/actions/accountingConfigurations';
import { trackEvent } from '../../../middleware/createAnalyticsMiddleware';
import { POWERBI_EMBEDDED_TOKEN_REFRESH_INTERVAL } from '../constants';
import { getAnalyticsType, getEventTimer, isFirstRender } from '../selectors/core';
import { openModal } from '../../app/actions/common';
import AnalyticsType from '../enums/analyticsType';
import { EventActionType } from '../enums/eventActionType';
import { EmbedData, IntegrityViolationCredentials, IReportBookmark, WithAnalyticsType, FilterItem } from '../types';
import {
    EMBEDDED_TOKEN_FETCHING,
    EMBEDDED_TOKEN_CLEARED,
    EMBEDDED_TOKEN_CONFIGURATION_FETCHING,
    POWERBI_ANALYTICS_TYPE_SET,
    UPDATE_EVENT_TIMER,
    BOOKMARK_CAPTURED,
    BOOKMARK_CLEARED,
    DATA_INTEGRITY_VIOLATED,
} from './actionTypes';

const EVENT_CATEGORY = 'PowerBi';

interface GetTokenResult {
    expirationUtc: string;
    token: string;
    tokenId: string;
}

export const fetchAccountingConfig = (): CommanderThunkAction => (dispatch) => {
    const enterpriseId = getCurrentEnterpriseId();
    dispatch(fetchAccountingConfiguration(enterpriseId));
};

export const fetchPowerBiEmbeddedToken =
    (): CommanderThunkAction =>
    async (dispatch, getState, { container }) => {
        const state = getState();
        const enterpriseId = getCurrentEnterpriseId();
        const analyticsType = getAnalyticsType(state);
        const promise = container()
            .powerBiComponent.getToken({ analyticsType, enterpriseId })
            .then((result: GetTokenResult) => ({ embeddedToken: result.token }));

        return dispatch(createAction(EMBEDDED_TOKEN_FETCHING, promise));
    };

export const [startFetchingPowerBiEmbeddedToken, stopFetchingPowerBiEmbeddedToken] = createPollerActions(
    fetchPowerBiEmbeddedToken,
    POWERBI_EMBEDDED_TOKEN_REFRESH_INTERVAL
);

export const clearPowerBiEmbeddedToken = (): CommanderThunkAction => (dispatch) =>
    dispatch(createAction(EMBEDDED_TOKEN_CLEARED));

export const fetchPowerBiEmbeddedTokenConfiguration =
    (): CommanderThunkAction =>
    async (dispatch, getState, { container }) => {
        const state = getState();
        const enterpriseId = getCurrentEnterpriseId();
        const analyticsType = getAnalyticsType(state);
        const promise = container()
            .powerBiComponent.getTokenParameters({ analyticsType, enterpriseId })
            .then(pick(['groupId', 'reportId', 'datasetId']));

        return dispatch(createAction(EMBEDDED_TOKEN_CONFIGURATION_FETCHING, promise));
    };

export const setAnalyticsType = (analyticsType: AnalyticsType) =>
    createAction(POWERBI_ANALYTICS_TYPE_SET, { analyticsType });

const updateEventTimer =
    (eventAction: EventActionType): CommanderThunkAction =>
    (dispatch, getState, { appClockService }) => {
        const runningEventTimer = getEventTimer(getState());
        const firstRender = isFirstRender(getState());

        /**
         * 1. save current time after pageSelected event
         * 2. after first reportRendered event, firstRender will become false
         * 3. cancel active timer on any other event than reportLoaded. User interactions (click, etc.) cause
         *    data reload and skew stats. We don't need to track those.
         */

        if (eventAction === EventActionType.PageSelected) {
            dispatch(
                createAction(UPDATE_EVENT_TIMER, {
                    runningEventTimer: appClockService.getAppNowUtc().millis,
                    firstRender,
                })
            );
        } else if (eventAction === EventActionType.ReportRendered && firstRender) {
            dispatch(createAction(UPDATE_EVENT_TIMER, { firstRender: false }));
        } else if (eventAction !== EventActionType.ReportLoaded && runningEventTimer !== null) {
            dispatch(
                createAction(UPDATE_EVENT_TIMER, {
                    runningEventTimer: null,
                    firstRender: false,
                })
            );
        }
    };

const getTimerResult = (eventAction: EventActionType, state: ApplicationState, appClockService: AppClockService) => {
    const runningEventTimer = getEventTimer(state);

    if (eventAction === EventActionType.ReportRendered && runningEventTimer) {
        const now = appClockService.getAppNowUtc().millis;
        const timerDifference = now - runningEventTimer;

        return {
            timeToRender: Math.round(timerDifference),
            firstRender: isFirstRender(state),
        };
    }

    return {};
};

const trackCommonEvent =
    ({
        eventAction,
        eventLabel,
        eventCategory = EVENT_CATEGORY,
        filters,
    }: {
        eventAction: EventActionType;
        eventLabel: string;
        eventCategory?: string;
        filters?: FilterItem[];
    }): CommanderThunkAction =>
    (dispatch, getState, { appClockService }) => {
        dispatch(
            trackEvent({
                eventCategory,
                eventAction,
                eventLabel,
                eventValue: undefined,
                options: {
                    ...getTimerResult(eventAction, getState(), appClockService),
                    selectedFilters: filters?.filter((item) => item.operator !== 'All'),
                },
            })
        );

        dispatch(updateEventTimer(eventAction));
    };

export const selectPage =
    ({ pageName, analyticsType }: { pageName: string } & WithAnalyticsType): CommanderThunkAction =>
    (dispatch) => {
        dispatch(
            trackCommonEvent({
                eventAction: EventActionType.PageSelected,
                eventLabel: `${analyticsType}/${pageName}`,
            })
        );
    };

export const loadReport =
    ({ analyticsType }: WithAnalyticsType): CommanderThunkAction =>
    (dispatch) => {
        dispatch(trackCommonEvent({ eventAction: EventActionType.ReportLoaded, eventLabel: analyticsType }));
    };

export const renderedReport =
    ({ analyticsType, pageName, reportFilters = [], pageFilters = [] }: WithAnalyticsType): CommanderThunkAction =>
    (dispatch) => {
        dispatch(
            trackCommonEvent({
                eventAction: EventActionType.ReportRendered,
                eventLabel: `${analyticsType}/${pageName}`,
                filters: pageFilters?.concat(reportFilters),
            })
        );
    };

export const buttonClicked =
    ({ analyticsType, pageName, title }: WithAnalyticsType): CommanderThunkAction =>
    (dispatch) => {
        dispatch(
            trackCommonEvent({
                eventAction: EventActionType.ButtonClicked,
                eventLabel: `${analyticsType}/${pageName}/${title}`,
            })
        );
    };

export const selectionChanged =
    ({ report, page, visual }: EmbedData): CommanderThunkAction =>
    (dispatch) => {
        dispatch(
            trackCommonEvent({
                eventAction: EventActionType.SelectionChanged,
                eventLabel: `${report}/${page}/${visual}`,
            })
        );
    };

export const visualClicked =
    ({ report, page, visual }: EmbedData): CommanderThunkAction =>
    (dispatch) => {
        dispatch(
            trackCommonEvent({
                eventAction: EventActionType.VisualClicked,
                eventLabel: `${report}/${page}/${visual}`,
            })
        );
    };

export const selectData =
    ({ report, page, visual }: EmbedData): CommanderThunkAction =>
    (dispatch) => {
        dispatch(
            trackCommonEvent({
                eventAction: EventActionType.DataSelected,
                eventLabel: `${report}/${page}/${visual}`,
            })
        );
    };

export const openImportAnalytics =
    (): CommanderThunkAction =>
    (dispatch, getState, { resolveRoute }) => {
        const url = resolveRoute(RouteName.AnalyticsImport);
        return dispatch(openModal(url));
    };

export const persistCapturedBookmark =
    ({
        analyticsType,
        capturedBookmark,
    }: { capturedBookmark: IReportBookmark } & WithAnalyticsType): CommanderThunkAction =>
    (dispatch) => {
        const profileId = getCurrentProfileId();
        dispatch(createAction(BOOKMARK_CAPTURED, { profileId, analyticsType, capturedBookmark }));
        dispatch(
            trackCommonEvent({
                eventCategory: BOOKMARK_CAPTURED,
                eventAction: EventActionType.Click,
                eventLabel: analyticsType,
            })
        );
        showSuccessAlert({ description: lt('AnalyticsFiltersMakeDefaultSuccessMessage') });
    };

export const clearCapturedBookmark =
    ({ analyticsType }: WithAnalyticsType): CommanderThunkAction =>
    (dispatch) => {
        const profileId = getCurrentProfileId();
        dispatch(createAction(BOOKMARK_CLEARED, { profileId, analyticsType }));
        dispatch(
            trackCommonEvent({
                eventCategory: BOOKMARK_CLEARED,
                eventAction: EventActionType.Click,
                eventLabel: analyticsType,
            })
        );

        showSuccessAlert({ description: lt('AnalyticsFiltersClearSuccessMessage') });
    };

export const violateDataIntegrity =
    ({ currentUserId, powerBiUserId, enterpriseId, tab }: IntegrityViolationCredentials): CommanderThunkAction =>
    (dispatch) => {
        dispatch(
            trackCommonEvent({
                eventCategory: DATA_INTEGRITY_VIOLATED,
                eventAction: EventActionType.Error,
                eventLabel: `CurrentUserId: ${currentUserId}, PowerBiUserId: ${powerBiUserId}, EnterpriseId: ${enterpriseId}, AnalyticsType: ${tab?.analyticsType}, TabName: ${tab?.displayName}`,
            })
        );
        return dispatch(createAction(DATA_INTEGRITY_VIOLATED));
    };
